/* ===========================================================================
   DRAWLZ SENTINEL  —  real-customer health reporting  —  v1 (2026-08-10)

   WHY THIS EXISTS, AND WHY IT IS NOT AN ERROR LOGGER.

   Every bug found on 2026-08-10 was SILENT. The wrong-size bug in particular
   threw its exception INSIDE Dawn's own onVariantChange, where it was caught
   and discarded — so the customer's console was clean, and a generic error
   logger (Sentry, Clarity, window.onerror) would have reported NOTHING while
   the store shipped smalls to people who ordered 3XL for months.

   So this does not primarily watch for errors. It ASSERTS the handful of
   conditions that have to be true for the money paths to work, on real page
   loads, and reports when one of them is false. It catches errors too, but
   that is the secondary job.

   WHERE IT REPORTS. Straight into GA4 through the tag you already run
   (G-7CHXJBWMGX via the server-side container). No new vendor, no new script
   host, no new subscription, no new privacy surface — it sends no customer
   data, only the name of the assertion that failed.

   HOW TO SEE THE DATA. In GA4: Reports -> Engagement -> Events. Look for
     dz_health_fail   — an assertion that should be true was false
     dz_js_error      — an uncaught exception or promise rejection
   Build an exploration on event_name + the `label` parameter to see which.
   Zero dz_health_fail events is the healthy state.

   SAFETY. Wrapped in try/catch throughout, deferred, capped, deduped, and it
   never touches the page. It cannot break the storefront: if it throws, it
   throws inside its own try and does nothing. It NEVER clicks anything -- the
   destructive size-click test lives in tools/health-check.js and is for you,
   not for customers.

   INSTALL. Upload as a theme asset, then add ONE line at the very end of
   snippets/content_for_footer.liquid:

     <script src="{{ 'drawlz-sentinel.js' | asset_url }}" defer="defer"></script>

   Delete that line to revert completely.
   ======================================================================== */
(function () {
  'use strict';

  var CFG = {
    /* Fraction of page loads that report. 1 = every load. Assertions are one
       cheap event per load and only fire on FAILURE, so 1 is affordable and
       gives the clearest signal. Drop to 0.25 if event volume ever matters. */
    sampleRate: 1,
    /* Hard cap per page load, so a pathological loop can never flood GA4. */
    maxEventsPerPage: 3,
    /* Wait for the theme's own boot to finish before asserting. The critical
       scripts are promoted at DOMContentLoaded and load over the network, so
       asserting immediately would report false failures on slow connections.
       3s is comfortably past the ~1.1s promotion window measured on 2026-08-10. */
    settleMs: 3000,
    scripts: ['global.min.js', 'pickup-availability.js', 'cart.js', 'cart-drawer.js', 'product-form.js']
  };

  var sent = 0;
  var seen = {};

  function sample() { return Math.random() < CFG.sampleRate; }

  /* Send through whichever Google tag is present. gtag reaches GA4 directly;
     dataLayer is the fallback for a GTM-only setup (needs a forwarding tag). */
  function report(eventName, label, detail) {
    try {
      if (sent >= CFG.maxEventsPerPage) return;
      var key = eventName + '|' + label;
      if (seen[key]) return;          /* dedupe within the page load */
      seen[key] = 1;
      sent++;

      var payload = {
        label: String(label).slice(0, 100),
        detail: String(detail == null ? '' : detail).slice(0, 200),
        page_type: pageType(),
        page_path: location.pathname.slice(0, 100),
        theme_id: (window.Shopify && window.Shopify.theme) ? String(window.Shopify.theme.id) : 'unknown'
      };

      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        payload.event = eventName;
        window.dataLayer.push(payload);
      }
    } catch (e) { /* reporting must never itself break anything */ }
  }

  function pageType() {
    try {
      var c = document.body.className || '';
      if (c.indexOf('template-product') !== -1) return 'product';
      if (c.indexOf('template-collection') !== -1) return 'collection';
      if (c.indexOf('template-index') !== -1) return 'home';
      if (c.indexOf('template-cart') !== -1) return 'cart';
      return 'other';
    } catch (e) { return 'unknown'; }
  }

  /* ------------------------------------------------------ error capture --- */

  try {
    window.addEventListener('error', function (e) {
      if (!e) return;
      /* Resource load failures surface here too and are noisy third-party
         chaff; only report real script exceptions. */
      if (e.message) report('dz_js_error', e.message, (e.filename || '') + ':' + (e.lineno || ''));
    });
    window.addEventListener('unhandledrejection', function (e) {
      var r = e && e.reason;
      report('dz_js_error', (r && (r.message || r)) || 'unhandled rejection', '');
    });
  } catch (e) {}

  /* -------------------------------------------------------- assertions ---- */

  function assertScriptChain() {
    /* THE wrong-size precondition. If pickup-availability has not upgraded,
       Dawn's onVariantChange throws before it writes the chosen variant into
       the cart form, and the customer buys the wrong size with no error shown.
       This one assertion is the whole reason this file exists. */
    var t = pageType();
    if (t === 'product' && document.querySelector('pickup-availability')) {
      if (!window.customElements || !window.customElements.get('pickup-availability')) {
        report('dz_health_fail', 'variant_picker_not_ready', 'pickup-availability not upgraded');
      }
    }
    /* The cart drawer's boot chain. If these are still lazy the first tap on
       the cart icon navigates away instead of opening the drawer. */
    for (var i = 0; i < CFG.scripts.length; i++) {
      var name = CFG.scripts[i], tag = null, list = document.querySelectorAll('script[src],script[data-src]');
      for (var j = 0; j < list.length; j++) {
        var v = list[j].getAttribute('src') || list[j].getAttribute('data-src') || '';
        if (v.indexOf(name) !== -1) { tag = list[j]; break; }
      }
      if (tag && !tag.getAttribute('src')) {
        report('dz_health_fail', 'script_still_lazy', name);
      }
    }
  }

  function assertCartDrawer() {
    var cd = document.querySelector('cart-drawer');
    if (cd && typeof cd.open !== 'function') {
      report('dz_health_fail', 'cart_drawer_not_ready', 'cart-drawer element not upgraded');
    }
  }

  function assertSizeBridge() {
    /* The silent 429. When the products feed is throttled the collection grid
       renders fine but "Ready in your size" simply never appears — invisible
       from the outside, which is exactly how it survived undetected. */
    if (pageType() !== 'collection') return;
    var grid = document.querySelector('#product-grid, .dzc-grid');
    if (!grid) return;
    var items = grid.querySelectorAll('.grid__item, [data-dzc-card]').length;
    var tagged = document.querySelectorAll('[data-dzc-card]').length;
    if (items > 0 && tagged < items) {
      report('dz_health_fail', 'size_labels_missing', tagged + '/' + items + ' tagged');
    }
  }

  function assertNoCorsFailures() {
    /* Apps calling the myshopify domain instead of the primary one. Each call
       fails CORS and eats the rate limit that the size feature depends on. */
    try {
      var bad = performance.getEntriesByType('resource').filter(function (r) {
        return r.name.indexOf('myshopify.com/products/') !== -1;
      }).length;
      if (bad > 0) report('dz_health_fail', 'cross_origin_product_calls', String(bad));
    } catch (e) {}
  }

  function assertPageRendered() {
    /* Cheap guard against a total layout collapse of the kind the .dzc-wrap
       class collision caused — the page rendered, but stacked at the top. */
    try {
      if (document.documentElement.scrollHeight < 800) {
        report('dz_health_fail', 'page_collapsed', document.documentElement.scrollHeight + 'px');
      }
    } catch (e) {}
  }

  /* --------------------------------------------------------------- boot --- */

  function run() {
    try {
      assertScriptChain();
      assertCartDrawer();
      assertSizeBridge();
      assertNoCorsFailures();
      assertPageRendered();
    } catch (e) { /* silent by design */ }
  }

  if (!sample()) return;
  /* Assert AFTER the theme has had time to boot, and only when the tab is
     actually visible — a backgrounded tab throttles timers and would produce
     false failures. */
  function schedule() {
    if (document.visibilityState === 'hidden') {
      document.addEventListener('visibilitychange', function once() {
        if (document.visibilityState !== 'hidden') {
          document.removeEventListener('visibilitychange', once);
          setTimeout(run, CFG.settleMs);
        }
      });
      return;
    }
    setTimeout(run, CFG.settleMs);
  }

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule);
})();
